<script setup lang="ts">
import HeartEmpty from '@/assets/icons/2States/HeartEmpty.vue';
import HeartFull from '@/assets/icons/2States/HeartFull.vue';
import ButtonEdit from '@/assets/icons/Buttons/ButtonEdit.vue';
import FaxIcon from '@/assets/icons/Other/FaxIcon.vue';
import LinkIcon from '@/assets/icons/Other/LinkIcon.vue';
import EmailIcon from '@/assets/icons/Suppliers/EmailIcon.vue';
import LocationIcon from '@/assets/icons/Suppliers/LocationIcon.vue';
import PhoneIcon from '@/assets/icons/Suppliers/PhoneIcon.vue';
import { useFetch } from '@/composables/useFetch.js';
import type { MessageSchema } from '@/i18n/index.js';
import { useEnvStore } from '@/stores/envStore.js';
import { useUserStore } from '@/stores/useUserStore';
import type { Supplier } from '@/types/api.js';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LoadingView from '../LoadingView.vue';

const toast = useToast();
const user = ref(useUserStore().user);
const envStore = useEnvStore();
const { fetchData } = useFetch();
const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const router = useRouter();
const confirm = useConfirm();

const supplier: Ref<Supplier | null> = ref(null);
const isFavorite: Ref<boolean> = ref(false);

const confirmDeletion = (event) => {
  confirm.require({
    target: event.currentTarget,
    message: t('profile.suppliers.deleteConfirmation'),
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('common.delete'),
    accept: () => {
      deleteSupplier();
    },
  });
};

async function toggleFavorite(supplierUuid: string) {
  if (!user.value) return;
  const restaurantId = user.value.restaurants[0].id;
  fetchData(`${useEnvStore().apiUrl}/restaurants/${restaurantId}/suppliers/${supplierUuid}/toggle-favorite`, 'PATCH').then((response) => {
    if (response.data) {
      // let toastText = '';
      if (response.data.status === 'added') {
        isFavorite.value = true;
        // toastText = t('profile.suppliers.favoriteSupplierAdded');
      } else {
        isFavorite.value = false;
        // toastText = t('profile.suppliers.favoriteSupplierRemoved');
      }
      // toast.add({
      //   severity: 'success',
      //   summary: t('common.success'),
      //   detail: toastText,
      //   life: 3000,
      // })
    } else {
      console.log(response.error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.suppliers.toogleFavoriteError'),
        life: 3000,
      });
    }
  });
}

async function deleteSupplier() {
  if (!user.value) return;
  const restaurantId = user.value.restaurants[0].id;
  fetchData(`${useEnvStore().apiUrl}/restaurants/${restaurantId}/suppliers/${supplier.value?.uuid}`, 'DELETE').then((response) => {
    if (response.data) {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('profile.suppliers.deleteSuccess'),
        life: 3000,
      });
      router.replace({ name: 'suppliers' });
    } else if (response.error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.suppliers.deleteError'),
        life: 3000,
      });
    }
  });
}

onMounted(async () => {
  const route = useRoute();
  fetchData(`${envStore.apiUrl}/suppliers/${route.params.uuid}`).then((response) => {
    if (response.data) {
      supplier.value = response.data as Supplier;
    } else if (response.error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.suppliers.getSuppliersError'),
        life: 3000,
      });
    }
  });

  fetchData(`${envStore.apiUrl}/restaurants/${user.value?.restaurants[0].id}/suppliers`).then((response) => {
    if (response.data) {
      if (response.data.find((supplier: Supplier) => supplier.uuid === route.params.uuid)) {
        isFavorite.value = true;
      }
    } else if (response.error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.suppliers.getSuppliersError'),
        life: 3000,
      });
    }
  });
});
</script>

<template>
  <main>
    <div v-if="supplier">
      <h2 :class="supplier.tags.length > 0 ? 'mb-2' : 'mb-4'">{{ supplier.name }}</h2>

      <div v-if="supplier.tags.length > 0" class="mb-6 flex gap-2 text-sm text-primary-800">
        <Tag v-for="tag in supplier.tags" :key="tag.id">
          {{ t('profile.suppliers.tags.' + tag.name) }}
        </Tag>
      </div>

      <div class="space-y-8">
        <div class="space-y-4">
          <div v-if="supplier.address" class="flex gap-4">
            <span class="block aspect-square w-6 shrink-0 text-primary-600"><LocationIcon /></span>
            <span>
              {{ supplier.address.address_line_1 }},
              {{ supplier.address.city.postal_code }}
              {{ supplier.address.city.name }}
            </span>
          </div>
          <div v-if="supplier.website" class="flex gap-4">
            <span class="block aspect-square w-6 shrink-0 text-primary-600"><LinkIcon /></span>
            <a :href="supplier.website || '#'" target="_blank" class="hover:text-primary-600">
              {{ supplier.website }}
            </a>
          </div>
          <div v-if="supplier.phone || supplier.phone_2 || supplier.phone_3" class="flex gap-4">
            <span class="block aspect-square w-6 shrink-0 text-primary-600"><PhoneIcon /></span>
            <span>
              <!-- filter(Boolean) permet de retirer les valeurs falsy (comme null, undefined ou des chaînes vides)  -->
              {{ [supplier.phone, supplier.phone_2, supplier.phone_3].filter(Boolean).join(', ') }}
            </span>
          </div>

          <div v-if="supplier.fax" class="flex gap-4">
            <span class="block aspect-square w-6 shrink-0 text-primary-600"><FaxIcon /></span>
            <span>
              {{ supplier.fax }}
            </span>
          </div>
          <div v-if="supplier.email" class="flex gap-4">
            <span class="block aspect-square w-6 shrink-0 text-primary-600"><EmailIcon /></span>
            <span>
              {{ [supplier.email, supplier.email_2].filter(Boolean).join(', ') }}
            </span>
          </div>
        </div>

        <div v-if="supplier.logo_url" class="flex justify-center">
          <img :src="supplier.logo_url" alt="{{ supplier.name }} logo" class="max-h-64 max-w-full object-contain" />
        </div>

        <Button v-if="!supplier.is_custom" class="w-full" @click="toggleFavorite(supplier.uuid)">
          <div class="mr-2 aspect-square w-4">
            <HeartFull v-if="isFavorite" />
            <HeartEmpty v-else />
          </div>

          <span v-if="isFavorite">Retirer des favoris</span>
          <span v-else>Ajouter aux favoris</span>
        </Button>

        <div v-else class="grid grid-cols-2 gap-2">
          <Button class="w-full" @click="router.push(`/suppliers/${supplier.uuid}/edit`)">
            <div class="mr-2 aspect-square w-4">
              <ButtonEdit />
            </div>
            <span>{{ t('common.edit') }}</span>
          </Button>
          <Button class="w-full" severity="danger" @click="confirmDeletion">
            <div class="mr-2 aspect-square w-4"><i class="pi pi-trash" /></div>
            <span>{{ t('common.delete') }}</span>
          </Button>
        </div>
      </div>
    </div>
    <div v-else>
      <LoadingView />
    </div>
  </main>
</template>
